import Head from "next/head"
import { format } from "date-fns"
import { TZDate, tzName } from "@date-fns/tz"
import { roomsHidden } from "lib/rooms"

// Open Graph / X card tags for a shared session, or for one presentation within it.
//
// Every session deep link resolves to the program guide, so without these all of them would scrape
// the same generic preview. `og:url` matters most: it is what Facebook keys its cache on, so a
// constant value would collapse every session into one cached card no matter how distinct the
// shared link was. It is passed in already normalized to the bare session URL (no filter or search
// params) so that a session has exactly one cached identity.
//
// These are rendered server-side from the session resolved in getServerSideProps — crawlers do not
// run JavaScript, but they do not need to. They are deliberately *not* kept in sync as the reader
// clicks around: no sharing path reads the live DOM. Share buttons build their URL from React
// state, and a hand-copied URL gets scraped fresh from the server.
export default function SessionShareMeta({ session, presentation, site, url, image, imageIsSessionSpecific = false }) {
    if (!session) return null

    const meetingName = site?.meeting?.meetingName
    // A presentation link leads with the presentation; the session it belongs to moves into the
    // description so the card still says when and where.
    const subject = presentation?.title || session.title
    const title = meetingName ? `${subject} | ${meetingName}` : subject
    const description = buildDescription(session, site, presentation)

    return (
        <Head>
            <title key="title">{title}</title>
            <meta name="description" content={description} key="description" />

            <meta property="og:type" content="website" key="og:type" />
            <meta property="og:title" content={title} key="og:title" />
            <meta property="og:description" content={description} key="og:description" />
            <meta property="og:url" content={url} key="og:url" />
            {meetingName && <meta property="og:site_name" content={meetingName} key="og:site_name" />}
            {image && <meta property="og:image" content={image} key="og:image" />}

            {/* X falls back to the og: tags for title/description/image but still needs a card type
                to pick a layout. The large card crops to 2:1, which mangles the meeting logo used as
                the fallback — so it is reserved for genuinely per-session artwork, and everything
                else gets the compact card. Facebook ignores this and picks a layout by image size. */}
            <meta
                name="twitter:card"
                content={image && imageIsSessionSpecific ? "summary_large_image" : "summary"}
                key="twitter:card"
            />
            <meta name="twitter:title" content={title} key="twitter:title" />
            <meta name="twitter:description" content={description} key="twitter:description" />
            {image && <meta name="twitter:image" content={image} key="twitter:image" />}
        </Head>
    )
}

// Emma's own `description`/`full_description` are null on most sessions, so the card leads with the
// facts an attendee actually needs — when, where, what kind of session — and appends the prose only
// when there is some.
function buildDescription(session, site, presentation) {
    const parts = []

    if (presentation) parts.push(session.title)

    const timezone = site?.meeting?.timezone?.value
    if (session.datetime_start && timezone) {
        const start = new Date(session.datetime_start)
        // `tzName(..., "short")` rather than date-fns's `z` token, which renders "GMT-7" where the
        // rest of the app shows "PDT" — see components/agenda/formattedTimeString.
        const time = format(new TZDate(start, timezone), "EEEE, MMMM d, y · h:mm a")
        parts.push(`${time} ${tzName(timezone, start, "short")}`)
    }
    if (session.venue?.name && !roomsHidden(site)) parts.push(session.venue.name)
    if (session.session_type) parts.push(session.session_type)

    const prose = stripHtml(
        (presentation ? presentation.description : null) || session.description || session.full_description || "",
    )
    if (prose) parts.push(prose)

    return truncate(parts.join(" · "), 300)
}

function stripHtml(value) {
    return value
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
}

function truncate(value, max) {
    if (value.length <= max) return value
    return value.slice(0, max - 1).trimEnd() + "…"
}
